import React, { useState, useEffect } from 'react';
import PropTypes from 'prop-types';
import withStyles from '@material-ui/core/styles/withStyles';
import GoogleMap from 'google-map-react';
import { LoadScript, Autocomplete } from '@react-google-maps/api';
import { ADGMModal, ADGMImage, ADGMTextField } from 'aurora-components';
import { keyEmptyCheck, isLatitude, isLongitude } from 'aurora-utils/utils';
import Typography from '@material-ui/core/Typography';
import Button from '@material-ui/core/Button';
import styles from './styles';

const defaultCoords = {
  lat: 24.4369542,
  lng: 54.3495832,
};

function ADGMSelectMap(props) {
  const { classes, zoom, locale, isModalOpen, handleCloseModal, onSubmitLocation, previousSelectedPin } = props;

  const coords = {
    lat: isLatitude(previousSelectedPin?.location?.lat)
      ? parseFloat(previousSelectedPin?.location?.lat)
      : defaultCoords.lat,
    lng: isLongitude(previousSelectedPin?.location?.lng)
      ? parseFloat(previousSelectedPin?.location?.lng)
      : defaultCoords.lng,
  };

  const [selectedPin, setSelectedPin] = useState(null);
  const [map, setMap] = useState(null);
  const [searchResult, setSearchResult] = useState('Result: none');

  const [selectedPlaceName, setSelectedPlaceName] = useState(null);

  useEffect(() => {
    if (map) {
      // Check if the SearchBox is ready
      const input = document.getElementById('search-box-input');
      const newSearchBox = new window.google.maps.places.SearchBox(input);
      newSearchBox.addListener('places_changed', () => {
        const places = newSearchBox.getPlaces();

        if (places.length === 0) return;
        const selectedPlace = places[0];
        if (selectedPlace && selectedPlace.geometry) {
          const { location } = selectedPlace.geometry;
          const latitude = location.lat();
          const longitude = location.lng();
          setSelectedPin({ lat: latitude, lng: longitude });
          setSelectedPlaceName(selectedPlace.formatted_address);
        }
      });
    }
  }, [map]);

  const fetchLocationName = (lat, lng) => {
    // Use the Google Maps Geocoding API to fetch the location name
    const apiUrl = `https://maps.googleapis.com/maps/api/geocode/json?latlng=${lat},${lng}&key=${process.env.REACT_APP_GOOGLE_API}`;

    fetch(apiUrl)
      .then(response => response.json())
      .then(data => {
        if (data.status === 'OK' && data.results.length > 0) {
          const name = data.results[0].formatted_address;
          setSelectedPlaceName(name);
        } else {
          setSelectedPlaceName('Location Name Not Found');
        }
      })
      .catch(error => {
        console.error('Error fetching location name:', error);
        setSelectedPlaceName('Error Fetching Location Name');
      });
  };

  const handleMapClick = ({ lat, lng }) => {
    // When the map is clicked, add a new pin at the clicked location
    setSelectedPin({ lat, lng });
    fetchLocationName(lat, lng);
  };

  const locationSumbit = () => {
    onSubmitLocation(selectedPlaceName, selectedPin);
    handleCloseModal();
  };
  const onLoads = autocomplete => {
    setSearchResult(autocomplete);
  };

  const Marker = () => {
    return (
      <>
        <svg xmlns='http://www.w3.org/2000/svg' width='12' height='24' viewBox='0 0 12 24' fill='none'>
          <path
            d='M12 6C12 2.691 9.309 0 6 0C2.691 0 0 2.691 0 6C0 8.967 2.167 11.431 5 11.91V24H7V11.91C9.833 11.432 12 8.968 12 6Z'
            fill='#D2374F'
          />
        </svg>
      </>
    );
  };

  const onPlaceChanged = () => {
    if (searchResult != null) {
      const places = searchResult.getPlace();
      if (places.length === 0) return;
      const selectedPlace = places;
      if (selectedPlace && selectedPlace.geometry) {
        const { location } = selectedPlace.geometry;
        const latitude = location.lat();
        const longitude = location.lng();
        setSelectedPin({ lat: latitude, lng: longitude });
        setSelectedPlaceName(selectedPlace.formatted_address);
      }
    }
  };
  useEffect(() => {
    if (previousSelectedPin) {
      setSelectedPin(coords);
      setSelectedPlaceName(previousSelectedPin?.name);
    }
  }, [previousSelectedPin]);

  return (
    <>
      <style>
        {`
        .pac-container {
          z-index: 9999; /* Set z-index to a very high value */
        }
      `}
      </style>
      <ADGMModal open={isModalOpen} className={classes.cancelModal}>
        <div className={classes.cancelModalContainer}>
          <ADGMImage
            name='cross.svg'
            alt='crossicon'
            className={classes.crossIconimg}
            onClick={handleCloseModal}
            aria-hidden='true'
          />
          <Typography variant='h6' component='h1'>
            {keyEmptyCheck('Please_select_your_exact_location')}
          </Typography>
          <Typography variant='h6' component='h2'>
            {keyEmptyCheck('Search_for_Road_Landmark_and_pin_your_exact_location')}
          </Typography>
          <LoadScript
            googleMapsApiKey={process.env.REACT_APP_GOOGLE_API} // Replace with your actual API key
            libraries={['places']}
            s
          >
            <div className={classes.mapArea}>
              <GoogleMap
                center={selectedPin || coords}
                zoom={zoom}
                bootstrapURLKeys={{
                  key: process.env.REACT_APP_GOOGLE_API, // Replace with your actual API key
                  language: locale,
                }}
                onGoogleApiLoaded={({ maps }) => setMap(maps)}
                onClick={handleMapClick}
                options={{
                  disableDefaultUI: true,
                }}
              >
                <>{selectedPin && <Marker lat={selectedPin.lat} lng={selectedPin.lng} />}</>
              </GoogleMap>
              <div className='custom-input-container'>
                <Autocomplete onPlaceChanged={onPlaceChanged} onLoad={onLoads}>
                  <ADGMTextField
                    customClasses={{ field: classes.inputTxt, input: classes.nonUAEInput }}
                    id='search-box-input'
                    autoComplete
                    value={selectedPlaceName || ''}
                    onChange={e => setSelectedPlaceName(e.target.value)}
                  />
                </Autocomplete>
              </div>
            </div>
          </LoadScript>
          <div className={classes.addItemButton}>
            <Button
              variant='contained'
              color='primary'
              onClick={locationSumbit}
              disabled={selectedPlaceName === null || selectedPin === null}
            >
              {keyEmptyCheck('submit')}
            </Button>
          </div>
        </div>
      </ADGMModal>
    </>
  );
}

ADGMSelectMap.propTypes = {
  classes: PropTypes.object.isRequired,
  zoom: PropTypes.number,
  locale: PropTypes.string,
  isModalOpen: PropTypes.bool.isRequired,
  handleCloseModal: PropTypes.func.isRequired,
  onSubmitLocation: PropTypes.func.isRequired,
  previousSelectedPin: PropTypes.object.isRequired,
};

ADGMSelectMap.defaultProps = {
  className: {},
  zoom: 10,
  locale: 'en',
  lat: 0,
  lng: 0,
  isMarker: false,
};

export default withStyles(styles)(ADGMSelectMap);
